import { Circle, CircleCheck, CircleDot } from '@parrot-software/design-system/icons';
import { cn } from '@parrot-software/utils';

import type { AccountRequirement } from '@identity/domain/types';

/**
 * One row of the progress list. `isCompleted` comes from the backend data, never from the position
 * of the row, and the label is a prop because the text belongs to the flow.
 */
export type ActivationStepItem = {
  isCompleted: boolean;
  label: string;
  requirement: AccountRequirement;
};

/** Visual state of a single row. */
type ActivationStepState = 'completed' | 'current' | 'pending';

export type ActivationStepListProps = {
  currentStep: AccountRequirement | 'DONE';
  steps: readonly ActivationStepItem[];
  variant?: 'compact' | 'sidebar';
};

const ICON_BY_STATE = {
  completed: CircleCheck,
  current: CircleDot,
  pending: Circle,
};

/**
 * The visual state of a row. Completed wins over current, so a requirement the backend already
 * reports as met is checked even during the render where its screen is still on screen.
 */
const stepStateFor = (step: ActivationStepItem, currentStep: AccountRequirement | 'DONE'): ActivationStepState => {
  if (step.isCompleted) return 'completed';
  if (step.requirement === currentStep) return 'current';

  return 'pending';
};

/**
 * Progress indicator of the invitation flow, and not a navigation menu: every step is already saved,
 * so there is nothing to go back to and no row is clickable.
 *
 * @param {ActivationStepListProps} props - Component properties
 */
export const ActivationStepList = ({ currentStep, steps, variant = 'sidebar' }: ActivationStepListProps) => {
  if (variant === 'compact') {
    const currentLabel = steps.find((step) => stepStateFor(step, currentStep) === 'current')?.label;

    return (
      <div className="flex items-center gap-3 font-sans" data-testid="activation-step-list-compact">
        <div className="flex items-center gap-1.5">
          {steps.map((step) => (
            <span
              className={cn('size-2 rounded-full bg-muted', {
                'bg-primary': stepStateFor(step, currentStep) !== 'pending',
              })}
              data-state={stepStateFor(step, currentStep)}
              data-testid={`activation-step-dot-${step.requirement}`}
              key={step.requirement}
            />
          ))}
        </div>
        {currentLabel && <span className="text-sm font-medium text-foreground">{currentLabel}</span>}
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-4 font-sans" data-testid="activation-step-list">
      {steps.map((step) => {
        const state = stepStateFor(step, currentStep);
        const Icon = ICON_BY_STATE[state];

        return (
          <li
            className={cn('flex items-center gap-3 text-sm text-primary-foreground/60', {
              'font-semibold text-primary-foreground': state === 'current',
            })}
            data-state={state}
            data-testid={`activation-step-${step.requirement}`}
            key={step.requirement}
          >
            <Icon aria-hidden className="size-5 shrink-0" />
            {step.label}
          </li>
        );
      })}
    </ul>
  );
};
